import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteLine from '@/assets/icons/delete_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import TextField from '@/components/atoms/TextField';
import { SCADA_FEED_LINE_MAX } from '@/domain/providerConfig';

import type { ScadaRow, ScadaRowProblems } from './scadaRows';
import { leavesRow } from './useScadaRows';

import styles from './ProviderSchemaField.module.scss';

/** What a caller needs to offer its own item picker next to an item id. */
export interface ItemIdBinding {
    value: string;
    onChange: (itemId: string) => void;
    label: string;
}

export interface ScadaItemRowProps {
    row: ScadaRow;
    rowId: string;
    rowName: string;
    fault: ScadaRowProblems | undefined;
    signals: readonly string[];
    disabled: boolean;
    itemPicker?: (binding: ItemIdBinding) => ReactNode;
    onUpdate: (change: Partial<ScadaRow>) => void;
    onRemove: () => void;
    onLeave: () => void;
}

/** One SCADA entry: its item id, signal and feed line. */
function ScadaItemRow({
    row,
    rowId,
    rowName,
    fault,
    signals,
    disabled,
    itemPicker,
    onUpdate,
    onRemove,
    onLeave,
}: ScadaItemRowProps) {
    const { t } = useTranslation();
    return (
        <fieldset
            className={styles.scadaRow}
            onBlur={(event) => {
                if (leavesRow(event.currentTarget, event.relatedTarget, document.hasFocus()))
                    onLeave();
            }}>
            <legend className={styles.rowLegend}>{rowName}</legend>
            <FormField label={t('providerConfig.scada.itemId')} htmlFor={`${rowId}-item`}>
                <TextField
                    className={styles.machineValue}
                    autoComplete="off"
                    spellCheck={false}
                    value={row.itemId}
                    invalid={fault?.itemId}
                    disabled={disabled}
                    onChange={(event) => onUpdate({ itemId: event.target.value })}
                />
                {itemPicker?.({
                    value: row.itemId.trim(),
                    onChange: (itemId) => onUpdate({ itemId }),
                    label: t('providerConfig.pens.pickItem', { subject: rowName }),
                })}
            </FormField>
            <FormField label={t('providerConfig.scada.signal')} htmlFor={`${rowId}-signal`}>
                <Select
                    value={row.signal}
                    invalid={fault?.signal}
                    disabled={disabled}
                    onChange={(event) => onUpdate({ signal: event.target.value })}>
                    <option value="" />
                    {signals.map((signal) => (
                        <option key={signal} value={signal}>
                            {t(`providerConfig.subjects.${signal}`)}
                        </option>
                    ))}
                </Select>
            </FormField>
            <FormField label={t('providerConfig.scada.feedLine')} htmlFor={`${rowId}-line`}>
                <TextField
                    maxLength={SCADA_FEED_LINE_MAX}
                    value={row.feedLine}
                    invalid={fault?.feedLine}
                    disabled={disabled}
                    onChange={(event) => onUpdate({ feedLine: event.target.value })}
                    // Trimmed on leaving the box, not per keystroke, so a space between words
                    // survives typing.
                    onBlur={() => {
                        const trimmed = row.feedLine.trim();
                        if (trimmed !== row.feedLine) onUpdate({ feedLine: trimmed });
                    }}
                />
            </FormField>
            <Button
                variant="danger"
                className={styles.rowAction}
                disabled={disabled}
                icon={<SvgIcon icon={DeleteLine} color="currentColor" />}
                onClick={onRemove}>
                {t('providerConfig.scada.remove')}
            </Button>
        </fieldset>
    );
}

export default ScadaItemRow;
export { ScadaItemRow };
